import React from "react";
import { backgroundQueryRefresh } from "../../../../../../shared/frontend/system/backgroundQueryRefresh";
import { useQuery } from "@tanstack/react-query";
import type { StudioWithAccess } from "../api/types";
import { useAuth } from "../auth/useAuth";
import { useI18n } from "../i18n/useI18n";
import { globalKey } from "../query/queryKeys";
import { authQueryScope } from "../query/authQueryScope";

const NO_STUDIOS: StudioWithAccess[] = [];

/**
 * Lädt alle Studios des Mandanten mit Zugriffsflag für den aktuellen User.
 * Studioauswahl anzeigen nur wenn:
 * - User hat Zugriff auf mehr als ein Studio, ODER
 * - Mandant hat mehrere Studios, aber User hat nicht auf alle Zugriff (Transparenz: sieht, warum er nicht bearbeiten kann).
 * Entspricht: Anzeige wenn studiosWithAccess.length > 1 (mehr als ein Studio im Mandanten).
 */
export function useStudiosWithAccess() {
  const auth = useAuth();
  const { text } = useI18n();
  const studios = useQuery({
    queryKey: globalKey("lookups", "studios-with-access", ...authQueryScope(auth.api, auth.info)),
    queryFn: ({ signal }) => auth.api.get<StudioWithAccess[]>("/api/v1/studios/with-access", { signal }),
    enabled: Boolean(auth.token) && !auth.info?.tenantImportCommitInProgress,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: query => backgroundQueryRefresh(query, "always"),
    retry: false
  });
  // Auch nach einem fehlgeschlagenen Rechteabgleich keine alten Zugriffsoptionen anbieten.
  const allStudiosWithAccess = studios.isError ? NO_STUDIOS : studios.data ?? NO_STUDIOS;
  // Manuelle Deaktivierung blendet aus; eine reine Lizenzsperre bleibt sichtbar.
  const studiosWithAccess = React.useMemo(
    () => allStudiosWithAccess.filter((studio) => studio.isActive !== false),
    [allStudiosWithAccess]
  );
  // Ausschließlich für die Studioverwaltung, damit Reaktivieren möglich bleibt.
  const allAllowedStudios = React.useMemo(
    () => allStudiosWithAccess.filter((studio) => studio.canAccess),
    [allStudiosWithAccess]
  );
  const loaded = !studios.isPending;
  const studiosLoadFailed = studios.isError;
  const { refetch: reloadStudios } = studios;
  const fetchStudios = React.useCallback(() => { void reloadStudios(); }, [reloadStudios]);

  const showStudioSelection = studiosWithAccess.length > 1;

  const noAccessLabel = text("No access – You have read-only or no access to this studio.");

  const optionsForSelect = React.useMemo(
    () =>
      studiosWithAccess.map((s) => ({
        value: s.id,
        // Gesperrte Standorte sind in der Auswahl sofort erkennbar.
        label: s.billingLockedAt ? `${s.name} (${text("locked")})` : s.name,
        disabled: !s.canAccess,
        title: !s.canAccess ? noAccessLabel : undefined
      })),
    [studiosWithAccess, noAccessLabel, text]
  );

  /** Nur Studios, auf die der User Zugriff hat (für Validierung / Default-Wert) */
  const allowedStudios = React.useMemo(
    () => studiosWithAccess.filter((s) => s.canAccess),
    [studiosWithAccess]
  );

  /** Nur zugreifbare Studios für Drawer-Auswahl (nicht zugreifbare werden nicht angezeigt) */
  const optionsOnlyAllowed = React.useMemo(
    () => optionsForSelect.filter((o) => !o.disabled),
    [optionsForSelect]
  );

  /** true, wenn User nicht auf alle Studios des Mandanten zugreifen kann (dann „alle Studios“-Einträge im Drawer read-only) */
  const isRestrictedUser = studiosWithAccess.length > 0 && allowedStudios.length < studiosWithAccess.length;

  /** Beim Anlegen: Standard-Studio-IDs für eingeschränkte User (nur ihre Studios), damit nicht versehentlich „alle Studios“ angelegt wird. Bei einem Studio-Mandanten undefined. */
  const defaultStudioIdsForCreate =
    showStudioSelection && isRestrictedUser ? allowedStudios.map((s) => s.id) : undefined;

  return {
    allAllowedStudios,
    studiosWithAccess,
    showStudioSelection,
    optionsForSelect,
    optionsOnlyAllowed,
    allowedStudios,
    isRestrictedUser,
    defaultStudioIdsForCreate,
    noAccessLabel,
    studiosLoaded: loaded,
    studiosLoadFailed,
    refetch: fetchStudios
  };
}
